import { IconName } from '@fortawesome/fontawesome-svg-core';
import { IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons/faChevronDown';
import AccordionSummary, {
  AccordionSummaryTypeMap as MUIAccordionItemHeaderProps,
} from '@mui/material/AccordionSummary';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { StyledFontAwesomeIconMui5 } from '../FontAwesomeIcon';
import { AccordionHeaderText } from './AccordionHeaderText';
import { FC } from 'react';

type CustomAccordionItemHeaderStyleProps = {
  isExpandable?: boolean;
  bg?: string;
};

const StyledAccordionSummary = styled(AccordionSummary, {
  skipSx: false,
  shouldForwardProp: (prop) => prop !== 'bg' && prop !== 'isExpandable',
})<{ bg: string; isExpandable: boolean }>(({ bg, isExpandable }) => ({
  width: '100%',
  backgroundColor: bg,
  justifyContent: 'space-between',
  pointerEvents: isExpandable ? 'auto' : 'none',
  paddingLeft: 0,
  paddingRight: 0,
  '& .MuiAccordionSummary-content': {
    alignItems: 'center',
    justifyContent: 'space-between',
  },
}));

const AdditionalInfoText = styled(Typography)(({ theme }) => ({
  '&.MuiTypography-root': {
    textTransform: 'uppercase',
    color: theme.palette.primary.main,
    fontSize: '16px',
    fontWeight: 'bold',
    paddingRight: theme.spacing(1),
  },
}));

const HeaderIconContainer = styled('div')(({ theme }) => ({
  marginRight: theme.spacing(1),
}));

type CustomAccordionItemHeaderProps = {
  icon?: IconName | IconDefinition;
  text: string | JSX.Element;
  isExpandable?: boolean;
  additionalInfo?: string;
  color?: string;
  bg?: string;
  sx?: any;
  className?: string;
};

export type AccordionItemHeaderProps = CustomAccordionItemHeaderProps &
  CustomAccordionItemHeaderStyleProps &
  Partial<MUIAccordionItemHeaderProps>;

export const AccordionItemHeader: FC<AccordionItemHeaderProps> = ({
  icon,
  text,
  additionalInfo,
  isExpandable = true,
  color,
  bg = 'white',
  sx,
  ...restProps
}): JSX.Element => {
  return (
    <StyledAccordionSummary
      bg={bg}
      sx={sx}
      isExpandable={isExpandable}
      expandIcon={
        <StyledFontAwesomeIconMui5
          sx={{ visibility: isExpandable ? 'visible' : 'hidden' }}
          icon={faChevronDown}
          color={color}
          size='sm'
        />
      }
      {...restProps}
    >
      {icon && (
        <HeaderIconContainer>
          <StyledFontAwesomeIconMui5
            icon={icon as IconName}
            fixedWidth
            color={color}
            sx={{ color: color }}
            size='lg'
          />
        </HeaderIconContainer>
      )}
      {typeof text === 'string' ? (
        <AccordionHeaderText>{text}</AccordionHeaderText>
      ) : (
        text
      )}
      {additionalInfo && (
        <AdditionalInfoText>{additionalInfo}</AdditionalInfoText>
      )}
    </StyledAccordionSummary>
  );
};
